<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { authApi } from '@/api/auth';
import { ApiException } from '@/api/request';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore } from '@/stores/region';
import { toast } from '@/utils/toast';

const props = defineProps<{ show: boolean }>();
const emit = defineEmits<{ (e: 'update:show', v: boolean): void; (e: 'logged-in'): void }>();

const auth = useAuthStore();
const region = useRegionStore();

// 模式：login（仅手机验证） / register（手机验证 + 扩展资料）
// 设计文档 F-FE-08 / F-FE-09：登录走手机验证；注册追加单位/职务/业务类型等必填
const mode = ref<'login' | 'register'>('login');
const title = computed(() => mode.value === 'login' ? '登录' : '注册');

/** 已完成手机号验证（登录通过但该号未注册 → 进入注册页补资料） */
const phoneVerified = ref(false);
/** 手机验证通过后补资料的有效期倒计时（秒），0 = 不计时 */
const graceLeft = ref(0);
let graceTimer: number | null = null;

function startGraceCountdown(sec: number) {
  graceLeft.value = sec;
  if (graceTimer) clearInterval(graceTimer);
  graceTimer = window.setInterval(() => {
    graceLeft.value--;
    if (graceLeft.value <= 0 && graceTimer) {
      clearInterval(graceTimer);
      graceTimer = null;
    }
  }, 1000);
}

// 公共字段：手机号 + 验证码 + 须知（两模式共用）
const phone = ref('');
const code = ref('');
const agree = ref(false);
const sending = ref(false);
const countdown = ref(0);
const hint = ref('');
const hintVisible = ref(false);

// 注册模式专属：扩展资料
const BIZ_TYPES = ['政府及事业单位', '资产投资', '咨询设计', '产业运营', '其他'];
const ASSETS = ['海洋', '林业', '矿产', '城市更新', '文旅', '产业园', '碳汇', '生态产品', '土地', '水'];

/**
 * 机会帐区域配额（独立双配额，与后端 config/quota.json 保持一致）
 *  - 数据库只记录「区县记录数上限」
 *  - 市级名额由系统按比例自动折算，与区县配额独立、互不占用
 *  - 用户自选：解锁哪些市、解锁哪些区县
 */
const COUNTY_QUOTA = 3;
/** 市级名额（后台按比例自动折算，此处仅用于前端提示展示） */
const CITY_CAPACITY = 1;

const unit = ref('');
const jobTitle = ref('');
const bizType = ref('');
const nickname = ref('');
const email = ref('');
const industry = ref('');
const concernedAssets = ref<string[]>([]);
const concernedRegions = ref<string[]>([]);
const agreedTermsVersion = ref('v1');

/**
 * 机会帐关注区域：按 1:3 额度模型自选
 *  - 点选城市（市级额度，1 市 = 3 额度）
 *  - 在已选城市下点选区县（每区县 1 额度）
 *  - 超出总额度的部分不选（服务端仍会兜底裁剪）
 */
const selectedCities = ref<string[]>([]);
const selectedCounties = ref<string[]>([]);

/** 21 个市（按车牌字母 A→W 排序，与区域选择器一致） */
const cityOptions = computed(() =>
  region.allRegions.filter(r => r.level === 'city')
);

/** 某市下的区县 */
function countiesOf(cityCode: string) {
  const codes = region.countiesByCity(cityCode);
  return region.allRegions.filter(r => codes.includes(r.code));
}

/** 已占用区县配额 */
const usedCountyQuota = computed(() => selectedCounties.value.length);
/** 剩余区县配额 */
const remainingCountyQuota = computed(() => COUNTY_QUOTA - usedCountyQuota.value);
/** 已选城市数 */
const usedCityCount = computed(() => selectedCities.value.length);
/** 剩余市级名额 */
const remainingCityCount = computed(() => CITY_CAPACITY - usedCityCount.value);

/** 已选城市下当前展开的市（用于点选其区县） */
const expandedCity = ref('');

function toggleCity(code: string) {
  const i = selectedCities.value.indexOf(code);
  if (i >= 0) {
    selectedCities.value.splice(i, 1);
    // 取消该市时，同时移除其下已选区县
    const names = countiesOf(code).map(c => c.name);
    selectedCounties.value = selectedCounties.value.filter(n => !names.includes(n));
    if (expandedCity.value === code) expandedCity.value = '';
    return;
  }
  if (remainingCityCount.value <= 0) {
    toast('已无可用的城市名额，如需查看更多城市请联系我们咨询顾问');
    return;
  }
  selectedCities.value.push(code);
  expandedCity.value = code;
}

function toggleCounty(name: string) {
  const i = selectedCounties.value.indexOf(name);
  if (i >= 0) {
    selectedCounties.value.splice(i, 1);
    return;
  }
  if (remainingCountyQuota.value <= 0) {
    toast('已无可用的区县配额，如需查看更多区县请联系我们咨询顾问');
    return;
  }
  selectedCounties.value.push(name);
}

// 用户须知与免责声明（远端拉取）
const notice = ref<{ version: string; title: string; summary: string; sections: any[] } | null>(null);
const noticeOpen = ref(false);

function reset() {
  mode.value = 'login';
  phoneVerified.value = false;
  if (graceTimer) { clearInterval(graceTimer); graceTimer = null; }
  graceLeft.value = 0;
  // 不清空 phone/code，便于切换模式时保留已填的手机号/验证码
}

watch(() => props.show, (s) => { if (s) { reset(); loadNotice(); } });

onMounted(loadNotice);
onUnmounted(() => { if (graceTimer) { clearInterval(graceTimer); graceTimer = null; } });

/** 区域选择器依赖行政区划清单（21 市 + 县区），确保已加载 */
async function ensureRegionData() {
  if (!region.adminLoaded) await region.loadAdminDivisions();
  if (!region.manifestLoaded) await region.loadManifest();
}
onMounted(ensureRegionData);
watch(() => props.show, (s) => { if (s) ensureRegionData(); });
async function loadNotice() {
  try {
    const r = await authApi.notice('v1');
    notice.value = r;
    agreedTermsVersion.value = r.version;
  } catch {
    notice.value = {
      version: 'v1',
      title: '用户须知与免责声明',
      summary: '登录即视为同意本须知全部条款。',
      sections: []
    };
  }
}

// 模式切换
function switchToLogin() {
  mode.value = 'login';
  phoneVerified.value = false;
  if (graceTimer) { clearInterval(graceTimer); graceTimer = null; }
  graceLeft.value = 0;
}
function switchToRegister() {
  mode.value = 'register';
  // 不重置 phone/code，保留上下文
}

// 提交校验
const loginOk = computed(() =>
  /^1\d{10}$/.test(phone.value.trim()) &&
  /^\d{4}$/.test(code.value.trim()) &&
  agree.value
);
const registerOk = computed(() =>
  loginOk.value &&
  unit.value.trim() &&
  jobTitle.value.trim() &&
  bizType.value
);

function getDeviceId() {
  let id = localStorage.getItem('nrap_device_id') ?? '';
  if (!id) {
    const c = (window as any).crypto;
    id = c?.randomUUID ? String(c.randomUUID()) : (Date.now().toString(36) + '-' + Math.random().toString(36).slice(2));
    localStorage.setItem('nrap_device_id', id);
  }
  return id;
}

function startCountdown(sec: number) {
  countdown.value = sec;
  const t = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) clearInterval(t);
  }, 1000);
}

/** 聚焦当前模式下的验证码输入框（登录/注册各一个） */
function focusCodeInput() {
  nextTick(() => {
    const inputs = document.querySelectorAll<HTMLInputElement>('.modal-body .code-row .form-input');
    const el = mode.value === 'register'
      ? inputs[inputs.length - 1]
      : inputs[0];
    el?.focus();
    el?.select();
  });
}

async function sendCode() {
  if (countdown.value > 0) return;
  if (!/^1\d{10}$/.test(phone.value.trim())) { toast('请输入正确的11位手机号'); return; }
  sending.value = true;
  try {
    const r = await authApi.sendCode(phone.value.trim(), getDeviceId());
    hintVisible.value = true;
    if (r.alreadySent) {
      // 60 秒内重复点击：服务端未重发，验证码此前已送达
      hint.value = r.message || '验证码已发送，请稍后再试';
      startCountdown(r.retryAfter || r.expireIn || 60);
    } else {
      if (r.provider === 'mock') {
        hint.value = `开发期：服务端已下发验证码 ${r.debugCode || ''}（mock 模式也可直接输入万能验证码 1234）`;
      } else if (r.provider === 'disabled') {
        hint.value = `演示模式：请输入万能验证码 ${r.debugCode || '1234'}（不发送短信）`;
      } else {
        hint.value = `验证码已发送至您的手机，${r.expireIn} 秒内有效`;
      }
      startCountdown(r.expireIn || 60);
    }
  } catch (e: unknown) {
    if (e instanceof ApiException && e.code === 4290) {
      // 验证码仍有效：按剩余秒数进入倒计时，避免用户盲等
      const ra = (e.data as { retryAfter?: number } | undefined)?.retryAfter ?? 60;
      hintVisible.value = true;
      hint.value = e.message;
      startCountdown(ra);
    } else {
      const msg = e instanceof Error ? e.message : '发送失败';
      toast('验证码发送失败：' + msg);
    }
  } finally {
    sending.value = false;
  }
}

async function doLogin() {
  if (!loginOk.value) return;
  try {
    const r = await authApi.login(phone.value.trim(), code.value.trim());
    if (r.needRegister) {
      // 该手机号未注册：提示并切换到注册界面（保留已填的手机号/验证码）
      toast('该手机号尚未注册，请完善登记信息');
      phoneVerified.value = true;
      startGraceCountdown(r.registerGraceSec || 300);
      switchToRegister();
      await nextTick();
      const el = document.querySelector('.modal-body');
      if (el) el.scrollTo({ top: 0, behavior: 'instant' as any });
    } else {
      // 已注册用户：直接登录成功（不需要再填任何资料）
      await auth.fetchMe();
      toast('登录成功，已解锁量化指标与完整机会账');
      emit('logged-in');
      emit('update:show', false);
    }
  } catch (e: unknown) {
    if (e instanceof ApiException && (e.code === 4002 || e.code === 4001)) {
      // 验证码错误/过期：清空输入并引导重新获取
      code.value = '';
      hintVisible.value = true;
      hint.value = e.message || '验证码错误，请重新获取';
      focusCodeInput();
    }
    const msg = e instanceof Error ? e.message : '登录失败';
    toast('登录失败：' + msg);
  }
}

async function doRegister() {
  if (!registerOk.value) return;
  try {
    // 若是直接点「立即注册 →」进入本页（未经登录验证），先补一次手机号验证：
    // 服务端要求先完成手机验证才允许提交登记（否则报「请先通过第一步手机验证」）。
    // 未注册号码的 login 会返回 needRegister=true，正是当前状态。
    if (!phoneVerified.value) {
      const r = await authApi.login(phone.value.trim(), code.value.trim());
      // 已注册（needRegister 缺失/false）→ 该号本来就有账号，直接登录即可
      if (!r.needRegister) {
        await auth.fetchMe();
        toast('该手机号已注册，已为你登录');
        emit('logged-in');
        emit('update:show', false);
        return;
      }
      phoneVerified.value = true;
      startGraceCountdown(r.registerGraceSec || 300);
    }

    const reg = await authApi.register({
      phone: phone.value.trim(),
      code: code.value.trim(),
      unit: unit.value.trim(),
      title: jobTitle.value.trim(),
      bizType: bizType.value,
      nickname: nickname.value.trim() || undefined,
      email: email.value.trim() || undefined,
      industry: industry.value.trim() || undefined,
      concernedAssets: concernedAssets.value,
      concernedRegions: concernedRegions.value,
      // 自选的机会帐区域：解锁的城市 + 区县（按 1:3 额度模型，服务端会兜底裁剪）
      selectedRegions: [
        ...selectedCities.value,
        ...selectedCounties.value
      ],
      agreedTermsVersion: agreedTermsVersion.value
    });
    await auth.fetchMe();
    if (reg.trimmed) {
      toast(reg.message || '区域配额不足，已按配额保留');
    } else {
      toast('登记完成，已解锁所选区域完整资料');
    }
    emit('logged-in');
    emit('update:show', false);
  } catch (e: unknown) {
    if (e instanceof ApiException) {
      // 4002 过期 / 4003 未先完成手机验证 / 4001 验证码错误
      // → 清空验证码并提示重新获取（宽限期已过或码被换掉）
      if (e.code === 4002 || e.code === 4003 || e.code === 4001) {
        code.value = '';
        hintVisible.value = true;
        hint.value = e.message || '验证码已失效，请重新获取';
        focusCodeInput();
      }
    }
    const msg = e instanceof Error ? e.message : '注册失败';
    toast('注册失败：' + msg);
  }
}

function toggleAsset(tag: string) {
  const i = concernedAssets.value.indexOf(tag);
  if (i >= 0) concernedAssets.value.splice(i, 1);
  else concernedAssets.value.push(tag);
}
</script>

<template>
  <div class="modal-mask" :class="{ show }" @click.self="emit('update:show', false)">
    <div class="modal modal-wide">
      <div class="modal-head">
        <div class="modal-title">{{ title }}</div>
        <button class="modal-close" @click="emit('update:show', false)">✕</button>
      </div>
      <div class="modal-body">

        <!-- ============ 登录界面 ============ -->
        <div v-if="mode === 'login'">
          <p class="login-tip">
            手机号验证即完成登录，登录后可查看量化指标与完整机会账。
          </p>

          <div class="form-item">
            <label class="form-label"><span class="req">*</span>手机号码</label>
            <input v-model="phone" class="form-input" type="tel" maxlength="11" placeholder="请输入11位手机号">
          </div>
          <div class="form-item">
            <label class="form-label"><span class="req">*</span>短信验证码</label>
            <div class="code-row">
              <input v-model="code" class="form-input" maxlength="4" placeholder="4位验证码">
              <button class="btn btn-ghost" :disabled="countdown > 0 || sending" @click="sendCode">
                {{ countdown > 0 ? `${countdown}s 后重发` : '获取验证码' }}
              </button>
            </div>
            <p v-if="hintVisible" class="code-hint">{{ hint }}</p>
          </div>

          <div class="form-item">
            <label class="notice-check">
              <input type="checkbox" v-model="agree">
              <span>我已阅读并同意</span>
              <a href="javascript:void(0)" @click="noticeOpen = !noticeOpen">
                《用户须知与免责声明》{{ notice ? `(${notice.version})` : '' }}
              </a>
            </label>
            <div v-if="noticeOpen" class="notice-panel">
              <h4 class="notice-title">{{ notice?.title || '用户须知与免责声明' }}</h4>
              <p class="notice-summary">{{ notice?.summary }}</p>
              <div v-for="s in notice?.sections" :key="s.id" class="notice-sec">
                <div class="notice-h">{{ s.id }}. {{ s.title }}</div>
                <p v-for="(p, i) in s.paragraphs" :key="i" class="notice-p">{{ p }}</p>
              </div>
            </div>
          </div>

          <button class="btn btn-primary btn-block" :disabled="!loginOk" @click="doLogin">
            登录
          </button>

          <div class="switch-row">
            还没有账号？
            <a href="javascript:void(0)" @click="switchToRegister">立即注册 →</a>
          </div>
        </div>

        <!-- ============ 注册界面 ============ -->
        <div v-else>
          <p class="login-tip">
            请填写完整的登记信息，便于平台为您提供对应区域与业务方向的服务对接。<br>
            <span class="muted">已为您手机号 <b>{{ phone }}</b> 自动生成账号；带 <i>*</i> 为必填项</span>
          </p>

          <!-- 手机号已验证提示（登录通过但该号未注册 → 补资料窗口） -->
          <p v-if="phoneVerified" class="verified-tip">
            ✓ 手机号已验证通过<span v-if="graceLeft > 0">，请在 <b>{{ Math.ceil(graceLeft / 60) }}</b> 分钟内完成登记</span>
          </p>

          <div class="form-grid-2">
            <label class="form-row">
              <span class="form-label"><span class="req">*</span>手机号码</span>
              <input v-model="phone" class="form-input" type="tel" maxlength="11" placeholder="请输入11位手机号">
            </label>
            <label class="form-row">
              <span class="form-label"><span class="req">*</span>短信验证码</span>
              <div class="code-row">
                <input v-model="code" class="form-input" maxlength="4" placeholder="4位验证码">
                <button class="btn btn-ghost" :disabled="countdown > 0 || sending" @click="sendCode">
                {{ countdown > 0 ? `${countdown}s 后重发` : '获取验证码' }}
              </button>
            </div>
            <p v-if="hintVisible" class="code-hint">{{ hint }}</p>
          </label>
            <label class="form-row">
              <span class="form-label">姓名 / 昵称 <span class="optional">（选填，便于称呼与识别）</span></span>
              <input v-model="nickname" class="form-input" type="text" maxlength="32" placeholder="如：张明 / 李女士">
            </label>
            <label class="form-row">
              <span class="form-label"><span class="req">*</span>单位</span>
              <input v-model="unit" class="form-input" type="text" maxlength="64" placeholder="如：阳春市自然资源局">
            </label>
            <label class="form-row">
              <span class="form-label"><span class="req">*</span>职务</span>
              <input v-model="jobTitle" class="form-input" type="text" maxlength="32" placeholder="如：副局长 / 投资总监">
            </label>
            <label class="form-row">
              <span class="form-label"><span class="req">*</span>业务类型</span>
              <select v-model="bizType" class="form-input">
                <option value="">请选择业务类型</option>
                <option v-for="b in BIZ_TYPES" :key="b" :value="b">{{ b }}</option>
              </select>
            </label>
            <label class="form-row">
              <span class="form-label">行业细分</span>
              <input v-model="industry" class="form-input" type="text" maxlength="32" placeholder="如：海洋牧场、矿业权、产业园">
            </label>
            <label class="form-row">
              <span class="form-label">邮箱</span>
              <input v-model="email" class="form-input" type="email" maxlength="64" placeholder="选填，便于发送资料">
            </label>
          </div>

          <!-- 关注资产类型（多选） -->
          <div class="form-item">
            <label class="form-label">关注资产类型（多选）</label>
            <div class="tag-picker">
              <button v-for="a in ASSETS" :key="a"
                      class="tag-chip"
                      :class="{ active: concernedAssets.includes(a) }"
                      @click="toggleAsset(a)">{{ a }}</button>
            </div>
          </div>

          <!-- 关注区域（机会帐区域配额） -->
          <div class="form-item">
            <label class="form-label">
              关注区域
              <span class="optional">（注册获取 1 个城市 + 3 个区县的详细机会帐资料，了解更多详情可联系我们咨询顾问获取更多区域信息）</span>
            </label>
            <div class="region-picker">
              <div class="quota-hint">
                可解锁：<b>{{ usedCityCount }}</b> / {{ CITY_CAPACITY }} 个城市 ·
                <b>{{ usedCountyQuota }}</b> / {{ COUNTY_QUOTA }} 个区县
                <span class="quota-left">
                  剩余 {{ remainingCityCount }} 城市 · {{ remainingCountyQuota }} 区县
                </span>
              </div>
              <div class="tag-picker tag-picker-wrap city-chips">
                <button v-for="c in cityOptions" :key="c.code"
                        class="tag-chip"
                        :class="{ active: selectedCities.includes(c.code), disabled: !selectedCities.includes(c.code) && remainingCityCount <= 0 }"
                        @click="toggleCity(c.code)">{{ c.name }}</button>
              </div>
              <div v-for="c in cityOptions.filter(x => selectedCities.includes(x.code))" :key="c.code" class="county-block">
                <div class="county-hint">
                  {{ c.name }} 下的区县（可少选）
                  <span class="county-toggle" @click="expandedCity = expandedCity === c.code ? '' : c.code">
                    {{ expandedCity === c.code ? '收起' : '展开' }}
                  </span>
                </div>
                <div v-if="expandedCity === c.code" class="tag-picker tag-picker-wrap">
                  <button v-for="cc in countiesOf(c.code)" :key="cc.code"
                          class="tag-chip"
                          :class="{ active: selectedCounties.includes(cc.name), disabled: !selectedCounties.includes(cc.name) && remainingCountyQuota <= 0 }"
                          @click="toggleCounty(cc.name)">{{ cc.name }}</button>
                </div>
                <p v-if="!countiesOf(c.code).length" class="county-empty">该市下暂无可查看的区县机会帐资料</p>
              </div>
              <p v-if="remainingCountyQuota <= 0 && remainingCityCount <= 0" class="county-tip">
                区域配额已用完，如需查看更多区域详情请联系我们咨询顾问
              </p>
            </div>
          </div>

          <!-- 须知确认 -->
          <div class="form-item">
            <label class="notice-check">
              <input type="checkbox" v-model="agree">
              <span>我已阅读并同意</span>
              <a href="javascript:void(0)" @click="noticeOpen = !noticeOpen">
                《用户须知与免责声明》{{ notice ? `(${notice.version})` : '' }}
              </a>
            </label>
            <div v-if="noticeOpen" class="notice-panel">
              <h4 class="notice-title">{{ notice?.title || '用户须知与免责声明' }}</h4>
              <p class="notice-summary">{{ notice?.summary }}</p>
              <div v-for="s in notice?.sections" :key="s.id" class="notice-sec">
                <div class="notice-h">{{ s.id }}. {{ s.title }}</div>
                <p v-for="(p, i) in s.paragraphs" :key="i" class="notice-p">{{ p }}</p>
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button class="btn btn-ghost" @click="switchToLogin">返回登录</button>
            <button class="btn btn-primary" :disabled="!registerOk" @click="doRegister">
              完成登记并登录
            </button>
          </div>

          <div class="switch-row">
            已有账号？
            <a href="javascript:void(0)" @click="switchToLogin">去登录 →</a>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-wide {
  width: 760px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px);
}
.modal-body {
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

.login-tip {
  font-size: 13.5px;
  color: var(--text-sub, #556664);
  margin-bottom: 18px;
  line-height: 1.8;
}
.login-tip .muted { color: var(--muted, #8a9a97); }
.login-tip b { color: var(--brand-dark, var(--brand-dark, #123527)); }

/* form rows */
.form-item { margin-bottom: 16px; }
.form-row { display: flex; flex-direction: column; gap: 6px; }
.form-row-full { grid-column: 1 / -1; }
.form-label {
  font-size: 13px; color: var(--text-sub, #556664);
  display: block; margin-bottom: 6px;
}
.form-label .req { color: #e05430; margin-right: 2px; }
.form-label .optional { color: var(--text-weak, #888); font-size: 11.5px; font-weight: 400; }
.form-input {
  font-family: inherit;
  font-size: 14px;
  color: var(--text-main, #2c3a38);
  padding: 9px 12px;
  border: 1px solid #dde4e2;
  border-radius: 8px;
  outline: none;
  background: #fff;
  width: 100%;
  transition: border-color .2s, box-shadow .2s;
}
.form-input:focus {
  border-color: var(--brand, #1c4d3a);
  box-shadow: 0 0 0 3px rgba(14, 159, 138, .12);
}

.code-row { display: flex; gap: 10px; align-items: stretch; }
.code-row .form-input { flex: 1; }
.code-row .btn { flex-shrink: 0; }
.verified-tip {
  margin: 0 0 14px;
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(11, 122, 106, 0.08);
  border-left: 3px solid #0b7a6a;
  color: #0b7a6a;
  font-size: 13px;
}
.verified-tip b { font-weight: 700; }
.code-hint {
  font-size: 12px;
  color: var(--brand-dark, #123527);
  background: rgba(232, 246, 244, .6);
  padding: 6px 10px;
  border-radius: 6px;
  margin-top: 6px;
}

.notice-check {
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; color: var(--text-sub, #556664);
  flex-wrap: wrap;
}
.notice-check a { color: var(--brand-dark, #123527); text-decoration: none; }
.notice-check a:hover { text-decoration: underline; }

/* 须知面板 */
.notice-panel {
  margin-top: 10px;
  padding: 14px 16px;
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #e4eae9;
  border-radius: 8px;
  background: #fafcfc;
  font-size: 12.5px;
  color: var(--text-sub, #556664);
  line-height: 1.85;
}
.notice-title {
  font-size: 14px; font-weight: 700;
  color: var(--text-main, #2c3a38);
  margin: 0 0 6px;
}
.notice-summary {
  margin: 0 0 10px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #e4eae9;
  color: var(--brand-dark, var(--brand-dark, #123527));
}
.notice-sec { margin-bottom: 8px; }
.notice-h { font-weight: 700; color: var(--text-main, #2c3a38); margin-bottom: 4px; }
.notice-p { margin: 0 0 4px; }

/* 2 列表单 */
.form-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;
}
.form-actions {
  display: flex; justify-content: flex-end; gap: 10px;
  margin-top: 18px;
}

/* 标签选择器 */
.tag-picker {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tag-picker-wrap { flex-wrap: wrap; }
.tag-chip {
  font-family: inherit;
  font-size: 12.5px;
  padding: 5px 12px;
  background: #fafafa;
  border: 1px solid #dde4e2;
  border-radius: 999px;
  cursor: pointer;
  color: var(--text-sub, #556664);
  transition: all .15s;
}
.tag-chip:hover { border-color: var(--brand, #1c4d3a); color: var(--brand-dark, #123527); }
.tag-chip.active {
  background: var(--brand, #1c4d3a);
  color: #fff;
  border-color: var(--brand, #1c4d3a);
}
/* 配额用尽后不可再选的置灰态 */
.tag-chip.disabled {
  opacity: .42;
  cursor: not-allowed;
  filter: grayscale(.6);
}
.tag-chip.disabled:hover { border-color: #dde4e2; color: inherit; }

/* 机会帐区域选择器：城市多选 + 区县点选（1:3 额度模型） */
.region-picker { display: flex; flex-direction: column; gap: 10px; }
.quota-hint {
  font-size: 12px;
  color: var(--text-weak, #8a9a97);
  line-height: 1.7;
  padding: 7px 10px;
  background: #f7faf8;
  border: 1px solid #e4ece8;
  border-radius: 6px;
}
.quota-hint b { color: var(--brand-dark, #123527); }
.quota-left { float: right; color: var(--brand, #1c4d3a); font-weight: 600; }
.city-chips { padding: 8px 10px; background: #fafcfb; border: 1px solid #e4ece8; border-radius: 8px; }
.county-block {
  padding: 10px 12px;
  background: #fafcfb;
  border: 1px solid #e4ece8;
  border-radius: 8px;
}
.county-hint {
  font-size: 12px;
  color: var(--text-weak, #8a9a97);
  margin-bottom: 8px;
  line-height: 1.6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}
.county-toggle {
  color: var(--brand, #1c4d3a);
  cursor: pointer;
  font-weight: 600;
  flex-shrink: 0;
}
.county-toggle:hover { text-decoration: underline; }
.county-empty { font-size: 12px; color: var(--text-weak, #8a9a97); margin: 6px 0 0; }
.county-tip {
  font-size: 11.5px;
  color: var(--hot, #c93d77);
  margin: 8px 0 0;
  line-height: 1.6;
}

/* 切换链接 */
.switch-row {
  margin-top: 14px;
  text-align: center;
  font-size: 13px;
  color: var(--text-sub, #556664);
}
.switch-row a {
  color: var(--brand-dark, #123527);
  text-decoration: none;
  font-weight: 600;
  margin-left: 4px;
}
.switch-row a:hover { text-decoration: underline; }

.btn { padding: 9px 18px; border-radius: 8px; font-size: 14px; cursor: pointer; }
.btn:disabled { opacity: .5; cursor: not-allowed; }
.btn-primary {
  background: var(--brand, #1c4d3a); color: #fff;
  border: none; font-weight: 600;
}
.btn-primary:hover:not(:disabled) { background: var(--brand-dark, #123527); }
.btn-ghost {
  background: #fff;
  border: 1px solid var(--brand, #1c4d3a);
  color: var(--brand-dark, #123527);
}
.btn-ghost:hover:not(:disabled) { background: rgba(14, 159, 138, .08); }
.btn-block { width: 100%; padding: 11px 18px; font-size: 15px; }
</style>